import type { Serializer } from '@vueuse/core';
import * as z from 'zod/v4-mini';

/**
 * Builds a `useStorage`/`useSessionStorage` serializer that validates persisted
 * JSON against a Zod schema, returning `fallback` when the stored value is
 * missing, malformed, or fails validation — so corrupt or stale storage can't
 * crash the caller. Pass a nullable `fallback` to model "no record yet".
 */
export function createZodSerializer<T extends z.ZodMiniType, Fallback = z.infer<T>>(
	schema: T,
	fallback: Fallback,
): Serializer<z.infer<T> | Fallback> {
	return {
		read(raw) {
			try {
				const parsed = z.safeParse(schema, JSON.parse(raw));
				return parsed.success ? parsed.data : fallback;
			}
			catch {
				return fallback;
			}
		},
		write(value) {
			return JSON.stringify(value);
		},
	};
}
